import type { ReactNode } from 'react';
import { SimulatedTag, type Source } from './data';

/** Titled frame around a chart: title, headline value or caption on the right. */
export function ChartFrame({
  title,
  meta,
  children,
  source,
  label,
}: {
  title: ReactNode;
  meta?: ReactNode;
  children: ReactNode;
  source?: Source;
  label?: string;
}) {
  return (
    <section className="hv-chart" aria-label={label ?? (typeof title === 'string' ? title : undefined)}>
      <div className="hv-chart__head">
        <span className="hv-chart__title">
          {title}
          {source === 'simulated' && <SimulatedTag />}
        </span>
        {meta && <span className="hv-chart__meta">{meta}</span>}
      </div>
      {children}
    </section>
  );
}

export interface Point {
  t: number; // epoch ms
  v: number;
}

export interface Annotation {
  t: number;
  label: string;
}

const W = 400;
const H = 120;
const PAD = { l: 28, r: 6, t: 8, b: 18 };

function scale(domain: [number, number], range: [number, number]) {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  return (x: number) => (d1 === d0 ? (r0 + r1) / 2 : r0 + ((x - d0) / (d1 - d0)) * (r1 - r0));
}

function hhmm(t: number): string {
  const d = new Date(t);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Line over a fixed time window, with optional threshold and annotations. */
export function LineChart({
  points,
  from,
  to,
  min,
  max,
  threshold,
  annotations = [],
  ariaLabel,
  formatY = (v) => String(Math.round(v)),
}: {
  points: Point[];
  from: number;
  to: number;
  min?: number;
  max?: number;
  threshold?: { value: number; label: string };
  annotations?: Annotation[];
  ariaLabel: string;
  formatY?: (v: number) => string;
}) {
  if (!points.length) return <div className="hv-chart__empty">No measurements in this window yet</div>;
  const vs = points.map((p) => p.v);
  const lo = min ?? Math.min(...vs, threshold?.value ?? Infinity);
  const hi = max ?? Math.max(...vs, threshold?.value ?? -Infinity);
  const x = scale([from, to], [PAD.l, W - PAD.r]);
  const y = scale([lo, hi === lo ? lo + 1 : hi], [H - PAD.b, PAD.t]);
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)} ${y(p.v).toFixed(1)}`).join('');
  const area = `${d}L${x(points[points.length - 1].t).toFixed(1)} ${H - PAD.b}L${x(points[0].t).toFixed(1)} ${H - PAD.b}Z`;
  const ticks = [from, (from + to) / 2, to];
  return (
    <svg className="hv-chart__svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel}>
      {[lo, (lo + hi) / 2, hi].map((v, i) => (
        <g key={i}>
          <line className="hv-chart__grid" x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} />
          <text className="hv-chart__axis" x={PAD.l - 4} y={y(v) + 3} textAnchor="end">
            {formatY(v)}
          </text>
        </g>
      ))}
      {ticks.map((t, i) => (
        <text key={i} className="hv-chart__axis" x={x(t)} y={H - 4} textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}>
          {hhmm(t)}
        </text>
      ))}
      {threshold && (
        <g>
          <line className="hv-chart__threshold" x1={PAD.l} x2={W - PAD.r} y1={y(threshold.value)} y2={y(threshold.value)} />
          <text className="hv-chart__threshold-label" x={PAD.l + 4} y={y(threshold.value) - 3}>
            {threshold.label}
          </text>
        </g>
      )}
      {annotations
        .filter((a) => a.t >= from && a.t <= to)
        .map((a, i) => (
          <g key={i}>
            <line className="hv-chart__marker" x1={x(a.t)} x2={x(a.t)} y1={PAD.t} y2={H - PAD.b} />
            <text className="hv-chart__marker-label" x={x(a.t) - 3} y={PAD.t + 9 + (i % 3) * 11} textAnchor="end">
              {a.label}
            </text>
          </g>
        ))}
      <path className="hv-chart__area" d={area} />
      <path className="hv-chart__line" d={d} />
    </svg>
  );
}

/** Bars per time bucket (e.g. rebuffer events per 5 minutes). */
export function BarChart({
  buckets,
  ariaLabel,
  warnAbove = 0,
}: {
  buckets: { t: number; v: number }[];
  ariaLabel: string;
  warnAbove?: number;
}) {
  if (!buckets.length) return <div className="hv-chart__empty">No measurements in this window yet</div>;
  const max = Math.max(1, ...buckets.map((b) => b.v));
  const bw = (W - PAD.l - PAD.r) / buckets.length;
  const y = scale([0, max], [H - PAD.b, PAD.t]);
  return (
    <svg className="hv-chart__svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel}>
      {[0, max].map((v, i) => (
        <g key={i}>
          <line className="hv-chart__grid" x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} />
          <text className="hv-chart__axis" x={PAD.l - 4} y={y(v) + 3} textAnchor="end">
            {v}
          </text>
        </g>
      ))}
      {buckets.map((b, i) => (
        <rect
          key={i}
          className={`hv-chart__bar${b.v > warnAbove ? ' hv-chart__bar--warn' : ''}`}
          x={PAD.l + i * bw + 2}
          width={Math.max(1, bw - 4)}
          y={b.v ? y(b.v) : H - PAD.b - 1}
          height={b.v ? H - PAD.b - y(b.v) : 1}
          rx="2"
        />
      ))}
      <text className="hv-chart__axis" x={PAD.l} y={H - 4}>
        {hhmm(buckets[0].t)}
      </text>
      <text className="hv-chart__axis" x={W - PAD.r} y={H - 4} textAnchor="end">
        {hhmm(buckets[buckets.length - 1].t)}
      </text>
    </svg>
  );
}
